import { Account, Transaction, BankHistory, BankHistoryChartData, AnalysisBounds } from '@algoan/dashboard/model';
import {
  convertDatetimeStringToDayTimestamp,
  haveSameSign,
  parseDailyBalanceDate,
  selectDate,
  sumTransactionsAmount,
  filterDailyBalancesOnAnalysisBounds,
  sumDailyBalancesFromAccounts,
} from '@algoan/dashboard/utils';
import { isSameDay } from 'date-fns';

const insertZeroBalanceIfNeeded = (history: BankHistory[]): BankHistory[] =>
  history.reduce<BankHistory[]>((accumulator, bankHistory) => {
    if (accumulator.length === 0) {
      return accumulator.concat(bankHistory);
    }

    const previousBankHistory = accumulator[accumulator.length - 1];

    const hasBalanceSignChanged = !haveSameSign(previousBankHistory.balance, bankHistory.balance);
    if (hasBalanceSignChanged) {
      const intermediatePoint: BankHistory = {
        difference: 0,
        date: (previousBankHistory.date + bankHistory.date) / 2,
        balance: 0,
      };
      return accumulator.concat(intermediatePoint).concat(bankHistory);
    } else {
      return accumulator.concat(bankHistory);
    }
  }, []);

/**
 * Convert Transactions and Accounts data to BankHistoryChartData format.
 */
export const calculateBankHistory = (
  transactions: Transaction[],
  accounts: Account[],
  analysisBounds?: AnalysisBounds,
): BankHistoryChartData => {
  if (accounts.length === 0) {
    return {
      history: [],
      positiveBalance: [],
      negativeBalance: [],
    };
  }

  const getAmountOfTransactionsOfFirstAnalysisDay = (): number => {
    const belongsToAccounts = (transaction: Transaction): boolean =>
      accounts.some((account) => account.id === transaction.accountId);
    const filteredTransactions = transactions.filter(belongsToAccounts);
    const transactionsOfFirstAnalysisDay = filteredTransactions.filter((transaction) =>
      isSameDay(new Date(selectDate(transaction.dates)), parseDailyBalanceDate(dailyBalances[0].date)),
    );

    return sumTransactionsAmount(transactionsOfFirstAnalysisDay);
  };

  const dailyBalances = filterDailyBalancesOnAnalysisBounds(sumDailyBalancesFromAccounts(accounts), analysisBounds);

  const history = dailyBalances.reduce<BankHistory[]>((accumulator, dailyBalance) => {
    const isFirstDailyBalance = accumulator.length === 0;
    const difference = isFirstDailyBalance
      ? getAmountOfTransactionsOfFirstAnalysisDay()
      : dailyBalance.balance - accumulator[accumulator.length - 1].balance;
    accumulator.push({
      date: convertDatetimeStringToDayTimestamp(dailyBalance.date),
      difference,
      balance: dailyBalance.balance,
    });
    return accumulator;
  }, []);

  /**
   * Since the balance area is defined by two charts (one for negative balance and
   * one for positive balance), we need to add points when balance changes from positive
   * to negative (and vice versa). Otherwise, the global chart would show a positive and
   * negative balance in the same time.
   */
  const historyForAreaCharts = insertZeroBalanceIfNeeded(history);

  return {
    history: history,
    // needed to draw positive balance area
    positiveBalance: historyForAreaCharts.map((bankHistory) => ({
      ...bankHistory,
      balance: bankHistory.balance <= 0 ? 0 : bankHistory.balance,
    })),
    // needed to draw negative balance area
    negativeBalance: historyForAreaCharts.map((bankHistory) => ({
      ...bankHistory,
      balance: bankHistory.balance > 0 ? 0 : bankHistory.balance,
    })),
  };
};
